import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { APP_SIDE_NAV_WIDTH } from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { Box, Tooltip, Typography } from "@mui/material";

const ICON_WIDTH = 24;

/**
 * A navigation item component that renders either a clickable menu item or a navigation link
 * based on the collapse state and whether there are child nav items or not.
 *
 * @param {Object} props - The component props
 * @param {string} [props.icon] - The icon identifier for the navigation item
 * @param {string} [props.path] - The navigation path/route for the item
 * @param {string} props.name - The display name of the navigation item
 * @param {boolean} [props.end=false] - Whether this is an end route for exact matching
 * @param {boolean} props.isCollapsed - Whether the navigation is in collapsed state
 * @param {string} [props.testId] - The test identifier for the component
 * @param {boolean} props.isActive - Whether this navigation item is currently active/selected
 * @param {boolean} [props.isSubNavItem=false] - Whether this is a sub-navigation item
 * @param {Function} [props.onClick] - Click handler function for the navigation item
 * @param {boolean} [props.hasChildItems=false] - Whether this item has child items
 * @param {boolean} [props.disabled=false] - Whether this item is disabled
 * @param {number} [props.count] - Optional count displayed beside the item
 * @returns {JSX.Element} The rendered navigation item component
 */
export const SideNavItem = ({
  icon,
  path,
  name,
  end = false,
  isCollapsed,
  testId,
  isActive,
  isSubNavItem = false,
  onClick,
  hasChildItems = false,
  disabled = false,
  count = 0,
}) => {
  const { classes, theme } = useStyles({ isCollapsed, isSubNavItem, disabled });
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setShowTooltip(false);
  }, [isCollapsed]);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    onClick();
  };

  return (
    <Tooltip
      title={name}
      open={showTooltip ? isCollapsed : false}
      onOpen={() => setShowTooltip(true)}
      onClose={() => setShowTooltip(false)}
      placement="right"
      enterDelay={250}
      arrow
    >
      <Box margin={theme.spacing(1, 0)}>
        {hasChildItems ? (
          <Box
            className={[
              classes.menuItem,
              classes.linkContainer,
              classes.itemHover,
            ]}
            onClick={handleClick}
            data-testid={testId || name}
          >
            <Box
              className={`${classes.linkContent} ${
                isActive ? classes.active : classes.inactive
              }`}
            >
              <Box display="flex" alignItems="center">
                {icon && (
                  <Box
                    minWidth={ICON_WIDTH}
                    maxWidth={ICON_WIDTH}
                    maxHeight={ICON_WIDTH}
                    minHeight={ICON_WIDTH}
                    overflow="hidden"
                  >
                    <MiIcon path={mdi[icon]} />
                  </Box>
                )}
                {!isCollapsed && (
                  <Typography
                    className={`${classes.text} ${
                      icon ? classes.textIcon : ""
                    }`}
                  >
                    {name}
                  </Typography>
                )}
                <Badge
                  count={count}
                  isCollapsed={isCollapsed}
                  isSubNavItem={isSubNavItem}
                />
              </Box>
              {!isCollapsed && hasChildItems && (
                <Box className={classes.arrowIcon}>
                  <MiIcon path={mdi.mdiChevronRight} size={0.9} />
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <NavItemLink
            path={path}
            name={name}
            icon={icon}
            end={end}
            testId={testId}
            onClick={handleClick}
            isCollapsed={isCollapsed}
            isActive={isActive}
            isSubNavItem={isSubNavItem}
            count={count}
          />
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * Represents an individual link in the navigation sidebar.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.path - The route path for navigation.
 * @param {string} props.name - The display name of the navigation link.
 * @param {string} props.icon - The icon name from MDI.
 * @param {boolean} [props.end=false] - Whether the route should match exactly.
 * @param {boolean} props.isCollapsed - Determines if the sidebar is collapsed.
 * @param {string} [props.testId] - Optional test ID for testing.
 * @returns {JSX.Element} A styled navigation link.
 */
const NavItemLink = ({
  path,
  name,
  icon,
  end,
  testId,
  isCollapsed,
  isActive,
  isSubNavItem = false,
  onClick = () => null,
  count,
}) => {
  const { classes } = useStyles({
    isCollapsed,
    isSubNavItem,
  });
  return (
    <NavLink
      to={path}
      end={end}
      data-testid={testId || name}
      className={classes.link}
      onClick={(e) => {
        if (!path) {
          e.preventDefault();
        }
        onClick();
      }}
    >
      <Box
        className={[classes.menuItem, classes.linkContainer, classes.itemHover]}
      >
        <Box
          className={`${classes.linkContentIcon} ${
            isActive ? classes.active : classes.inactive
          }`}
        >
          {icon && (
            <Box
              minWidth={ICON_WIDTH}
              maxWidth={ICON_WIDTH}
              maxHeight={ICON_WIDTH}
              minHeight={ICON_WIDTH}
              overflow="hidden"
            >
              <MiIcon path={mdi[icon]} />
            </Box>
          )}

          {!isCollapsed && (
            <Box flexGrow={1}>
              <Typography
                className={`${classes.text} ${icon ? classes.textIcon : ""}`}
              >
                {name}
              </Typography>
            </Box>
          )}
          <Badge
            count={count}
            isCollapsed={isCollapsed}
            isSubNavItem={isSubNavItem}
          />
        </Box>
      </Box>
    </NavLink>
  );
};

const Badge = ({ count, isCollapsed, isSubNavItem, disabled }) => {
  const { classes } = useStyles({
    isCollapsed,
    isSubNavItem,
    disabled,
  });
  if (isCollapsed || count == null || count === 0) return null;
  return (
    <Box className={classes.badge}>
      <Typography className={classes.count}>{count}</Typography>
    </Box>
  );
};

const useStyles = makeStyles()(
  (theme, { isCollapsed, isSubNavItem, disabled }) => ({
    link: {
      textDecoration: "none",
      color: theme.palette.grey.main,
    },
    linkContainer: {
      display: "flex",
      justifyContent: "flex-start",
      width: isCollapsed ? 40 : "auto",
      maxWidth: isCollapsed ? 40 : "100%",
      minWidth: isCollapsed
        ? 0
        : APP_SIDE_NAV_WIDTH - parseInt(theme.spacing(5), 10),
      borderRadius: 4,
      boxSizing: "border-box",
      transition: "all 200ms ease-in-out",
      height: 38,
      margin: isSubNavItem ? theme.spacing(0, 2) : 0,
    },
    itemHover: {
      "&:hover": {
        backgroundColor: isSubNavItem
          ? theme.palette.grey.accent
          : theme.palette.grey.mediumLight,
        color: theme.palette.primary.grey,
      },
    },
    active: {
      transition: "all 200ms ease-in-out",
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.accent}`,
      borderRadius: 4,
    },
    inactive: {
      border: `1px solid transparent`,
    },
    linkContent: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: theme.spacing(1.5, 0, 1.5, 1.5),
      width: "100%",
      color: "inherit",
      justifyContent: "space-between",
      position: "relative",
    },
    linkContentIcon: {
      flex: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: isSubNavItem ? theme.spacing(1.5, 0) : theme.spacing(1.5),
      color: "inherit",
      justifyContent: "center",
      position: "relative",
    },
    menuItem: {
      width: "100%",
      color: disabled ? theme.palette.grey.darkAccent : theme.palette.grey.main,
      cursor: disabled ? "default" : "pointer",
      borderRadius: 4,
    },
    text: {
      ...theme.typography.body2,
      maxWidth: APP_SIDE_NAV_WIDTH - parseInt(theme.spacing(5.5), 10),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: disabled
        ? theme.palette.grey.darkAccent
        : theme.palette.bluegrey.dark,
      paddingLeft: theme.spacing(2),
      flexGrow: 1,
    },
    textIcon: {
      maxWidth:
        APP_SIDE_NAV_WIDTH - ICON_WIDTH - parseInt(theme.spacing(12), 10),
    },
    arrowIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    count: {
      ...theme.typography.caption,
      minWidth: 15,
      textAlign: "center",
      color: theme.palette.common.white,
    },
    badge: {
      position: "absolute",
      top: -3,
      right: -6,
      borderRadius: 20,
      padding: `0 ${theme.spacing(0.7)}`,
      background: theme.palette.red.main,
      color: theme.palette.common.white,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
  })
);
